import { useState, useMemo, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Plus, Trash2, X, Shield } from 'lucide-react';
import { Tooltip } from '@/Components/ui/Tooltip';
import { useDeliveryProofForm } from '@/features/delivery-proof/hooks/useDeliveryProofForm';
import { useDeliveryProofStore } from '@/features/delivery-proof/stores/deliveryProofStore';
import { useAvailableWarranties, type AvailableWarranty } from '@/features/delivery-proof/hooks/useAvailableWarranties';
import type { WarrantyItemDto } from '@/features/delivery-proof/types/delivery-proof.types';
import { useCurrentUserWording } from '@/hooks/useVendorWording';
import type { WizardStepHandle } from '@/Components/shared/WizardLayout.types';
import { ToggleInput } from '@/Components/shared/ToggleInput';
import { InputWithAddon } from '@/Components/shared/InputWithAddon';

export const Step2WarrantySelection = forwardRef<WizardStepHandle>((_props, ref) => {
  const wording = useCurrentUserWording();
  const { nextStep, updateFormData } = useDeliveryProofForm();
  const deliveryProofId = useDeliveryProofStore((state) => state.deliveryProofId);
  const formData = useDeliveryProofStore((state) => state.formData);

  const [showWarrantyBlock, setShowWarrantyBlock] = useState(formData.warranties.length > 0);
  // Exclure la garantie vendue (isAccepted=true) du state local — elle est gérée séparément
  const internalWarranties = formData.warranties.filter((w) => !w.isExternal && !w.isAccepted);
  const [selectedWarranties, setSelectedWarranties] = useState<Map<number, 'proposed'>>(
    new Map(
      internalWarranties.map((w) => [w.warrantyId!, 'proposed'] as const)
    )
  );
  const [externalWarranties, setExternalWarranties] = useState<WarrantyItemDto[]>(
    formData.warranties.filter((w) => w.isExternal)
  );
  const [hidePrices, setHidePrices] = useState(formData.hidePrices);
  const [showExternalModal, setShowExternalModal] = useState(false);
  const [externalForm, setExternalForm] = useState({
    externalName: '',
    externalKmLimit: '',
    externalFinancialCap: '',
    externalDuration: '',
    externalPieces: '',
    externalPrice: '',
  });

  const { data: warranties, isLoading } = useAvailableWarranties(deliveryProofId);

  // Dérivation pure : formater le prix
  const formatPrice = useMemo(
    () => (warranty: AvailableWarranty) => {
      if (hidePrices) return '';

      if (warranty.isMensualisable && !warranty.isCashPayment) {
        return `${warranty.duration} x ${warranty.sellingPrice}€ TTC`;
      }
      return `${warranty.sellingPrice}€ TTC`;
    },
    [hidePrices]
  );

  // Garantie vendue
  const soldWarranty = useMemo(
    () => warranties?.find((w) => w.isSold),
    [warranties]
  );

  // Toutes les garanties non-vendues (pour le tableau)
  const otherWarranties = useMemo(
    () => warranties?.filter((w) => !w.isSold) || [],
    [warranties]
  );

  const handleToggleProposed = (warrantyId: number) => {
    setSelectedWarranties((prev) => {
      const next = new Map(prev);
      if (next.has(warrantyId)) {
        next.delete(warrantyId);
      } else {
        next.set(warrantyId, 'proposed');
      }
      return next;
    });
  };

  const handleAddExternal = () => {
    const newExternal: WarrantyItemDto = {
      isExternal: true,
      isAccepted: false,
      externalName: externalForm.externalName || undefined,
      externalKmLimit: externalForm.externalKmLimit || undefined,
      externalFinancialCap: externalForm.externalFinancialCap || undefined,
      externalDuration: externalForm.externalDuration || undefined,
      externalPieces: externalForm.externalPieces || undefined,
      externalPrice: externalForm.externalPrice || undefined,
    };
    setExternalWarranties((prev) => [...prev, newExternal]);
    setShowExternalModal(false);
    setExternalForm({
      externalName: '',
      externalKmLimit: '',
      externalFinancialCap: '',
      externalDuration: '',
      externalPieces: '',
      externalPrice: '',
    });
    if (!showWarrantyBlock) {
      setShowWarrantyBlock(true);
    }
  };

  const handleRemoveExternal = (index: number) => {
    setExternalWarranties((prev) => prev.filter((_, i) => i !== index));
  };

  // Formater le libellé d'une garantie pour le tableau
  const formatWarrantyLabel = (warranty: AvailableWarranty) => {
    const parts = [warranty.name, `${warranty.duration} mois`];
    const price = formatPrice(warranty);
    if (price) parts.push(price);
    return parts.join(' - ');
  };

  // Synchronise l'état local vers le store
  const syncToStore = () => {
    // Garanties proposées par l'utilisateur
    const proposedList: WarrantyItemDto[] = Array.from(selectedWarranties.keys()).map(
      (warrantyId) => ({
        warrantyId,
        isAccepted: false,
      })
    );

    // Garantie vendue = toujours acceptée
    const soldList: WarrantyItemDto[] = soldWarranty
      ? [{ warrantyId: soldWarranty.companyContractId, isAccepted: true }]
      : [];

    const allWarranties = [...soldList, ...proposedList, ...externalWarranties];

    updateFormData({
      warranties: showWarrantyBlock ? allWarranties : [],
      hidePrices: showWarrantyBlock ? hidePrices : false,
    });
  };

  // Sync live vers le store pour la preview PDF
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }
    syncToStore();
  }, [selectedWarranties, externalWarranties, hidePrices, showWarrantyBlock]);

  useImperativeHandle(ref, () => ({
    handleNext: () => handleSubmit(),
    handleSave: () => syncToStore(),
  }));

  // Suivant : navigation locale uniquement (pas d'appel API)
  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    syncToStore();
    nextStep();
  };

  return (
    <div className="tw-max-w-6xl tw-mx-auto tw-p-2 sm:tw-p-4">
      <div className="tw-p-4 sm:tw-p-6 tw-space-y-5">
      <form id="wizard-step-form" onSubmit={handleSubmit} className="tw-space-y-6">
        {/* Checkbox d'activation du bloc */}
        <label id="dp-warranty-toggle" className="tw-flex tw-items-start tw-gap-3 tw-cursor-pointer tw-select-none">
          <input
            type="checkbox"
            checked={showWarrantyBlock}
            onChange={(e) => setShowWarrantyBlock(e.target.checked)}
            className="tw-mt-0.5 tw-w-4 tw-h-4 tw-accent-affinicar-primary tw-cursor-pointer"
          />
          <span className="tw-text-sm tw-text-gray-700">
            Intégrer le bloc &quot;Informations Garanties&quot; dans l'attestation de livraison (facultatif)
          </span>
        </label>

        {showWarrantyBlock && (
          <div id="dp-warranty-list">
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4 tw-pb-3 tw-border-b tw-border-gray-100">
              <Shield className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
              <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Informations {wording.plural}</h4>
            </div>

            <div className="tw-space-y-4">
              {/* Toggle : Masquer les prix */}
              <ToggleInput
                label="Masquer les prix"
                value={hidePrices}
                onChange={setHidePrices}
                className="tw-px-3 tw-py-1.5 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200 tw-w-fit tw-min-w-[220px]"
              />

              {/* Tableau des garanties */}
              {isLoading ? (
                <div className="tw-p-4 tw-text-center tw-text-gray-500">
                  Chargement {wording.desPlural}...
                </div>
              ) : (
                <>
                  {/* ===== Desktop : tableau classique (hidden < sm) ===== */}
                  <div className="tw-hidden sm:tw-block tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-sm tw-border tw-border-gray-200 tw-rounded-lg">
                      <thead>
                        <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                          <th className="tw-text-left tw-px-4 tw-py-3 tw-font-medium tw-text-gray-700">
                            {wording.singular}
                          </th>
                          <th className="tw-text-center tw-px-4 tw-py-3 tw-font-medium tw-text-gray-700 tw-w-28">
                            <span className="tw-inline-flex tw-items-center">
                              Proposée(s)
                              <Tooltip content="Sélectionnez les garanties qui ont été proposées à votre client lors de la vente. Ces dernières apparaîtront comme ayant été refusées sur l'attestation de livraison." />
                            </span>
                          </th>
                          <th className="tw-text-center tw-px-4 tw-py-3 tw-font-medium tw-text-gray-700 tw-w-28">
                            <span className="tw-inline-flex tw-items-center">
                              Acceptée
                              <Tooltip content="Il s'agit de la garantie qui a été validée et acceptée par votre client." />
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {soldWarranty && (
                          <tr className="tw-border-b tw-border-gray-100 tw-bg-green-50/30">
                            <td className="tw-px-4 tw-py-3 tw-text-gray-900">
                              {formatWarrantyLabel(soldWarranty)}
                            </td>
                            <td className="tw-px-4 tw-py-3 tw-text-center">
                              <input type="checkbox" disabled checked={false} className="tw-w-4 tw-h-4 tw-cursor-not-allowed tw-opacity-50" />
                            </td>
                            <td className="tw-px-4 tw-py-3 tw-text-center">
                              <input type="checkbox" disabled checked className="tw-w-4 tw-h-4 tw-accent-green-600 tw-cursor-not-allowed" />
                            </td>
                          </tr>
                        )}

                        {otherWarranties.map((warranty) => (
                          <tr key={warranty.companyContractId} className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50/50">
                            <td className="tw-px-4 tw-py-3 tw-text-gray-900">
                              {formatWarrantyLabel(warranty)}
                            </td>
                            <td className="tw-px-4 tw-py-3 tw-text-center">
                              <input
                                type="checkbox"
                                checked={selectedWarranties.has(warranty.companyContractId)}
                                onChange={() => handleToggleProposed(warranty.companyContractId)}
                                className="tw-w-4 tw-h-4 tw-accent-affinicar-primary tw-cursor-pointer"
                              />
                            </td>
                            <td className="tw-px-4 tw-py-3 tw-text-center">
                              <input type="checkbox" disabled checked={false} className="tw-w-4 tw-h-4 tw-cursor-not-allowed tw-opacity-50" />
                            </td>
                          </tr>
                        ))}

                        {externalWarranties.map((ext, index) => (
                          <tr key={`ext-${index}`} className="tw-border-b tw-border-gray-100">
                            <td className="tw-px-4 tw-py-3 tw-text-gray-900">
                              <div className="tw-flex tw-items-center tw-gap-2">
                                <span>
                                  {[ext.externalName, ext.externalDuration ? `${ext.externalDuration} mois` : '', !hidePrices && ext.externalPrice ? `${ext.externalPrice}€ TTC` : ''].filter(Boolean).join(' - ') || 'Garantie externe'}
                                </span>
                                <span className="tw-text-xs tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-gray-100 tw-text-gray-500">Externe</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveExternal(index)}
                                  className="tw-text-red-400 hover:tw-text-red-600 tw-p-0.5 tw-transition-colors"
                                  title="Supprimer"
                                >
                                  <Trash2 className="tw-w-3.5 tw-h-3.5" />
                                </button>
                              </div>
                            </td>
                            <td className="tw-px-4 tw-py-3 tw-text-center">
                              <input type="checkbox" disabled checked className="tw-w-4 tw-h-4 tw-cursor-not-allowed tw-opacity-50" />
                            </td>
                            <td className="tw-px-4 tw-py-3 tw-text-center">
                              <input type="checkbox" disabled checked={false} className="tw-w-4 tw-h-4 tw-cursor-not-allowed tw-opacity-50" />
                            </td>
                          </tr>
                        ))}

                        {!soldWarranty && otherWarranties.length === 0 && externalWarranties.length === 0 && (
                          <tr>
                            <td colSpan={3} className="tw-px-4 tw-py-6 tw-text-center tw-text-gray-400">
                              Aucune {wording.singularLower} disponible
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* ===== Mobile : cards empilées (visible < sm) ===== */}
                  <div className="sm:tw-hidden tw-space-y-3">
                    {soldWarranty && (
                      <div className="tw-p-3 tw-bg-green-50/30 tw-rounded-lg tw-border tw-border-gray-200">
                        <p className="tw-text-sm tw-text-gray-900 tw-mb-2">{formatWarrantyLabel(soldWarranty)}</p>
                        <div className="tw-flex tw-items-center tw-gap-4">
                          <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-500">
                            <input type="checkbox" disabled checked={false} className="tw-w-5 tw-h-5 tw-cursor-not-allowed tw-opacity-50" />
                            Proposée
                          </label>
                          <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-green-700 tw-font-medium">
                            <input type="checkbox" disabled checked className="tw-w-5 tw-h-5 tw-accent-green-600 tw-cursor-not-allowed" />
                            Acceptée
                          </label>
                        </div>
                      </div>
                    )}

                    {otherWarranties.map((warranty) => (
                      <div key={warranty.companyContractId} className="tw-p-3 tw-bg-white tw-rounded-lg tw-border tw-border-gray-200">
                        <p className="tw-text-sm tw-text-gray-900 tw-mb-2">{formatWarrantyLabel(warranty)}</p>
                        <div className="tw-flex tw-items-center tw-gap-4">
                          <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-500 tw-cursor-pointer tw-min-h-[44px]">
                            <input
                              type="checkbox"
                              checked={selectedWarranties.has(warranty.companyContractId)}
                              onChange={() => handleToggleProposed(warranty.companyContractId)}
                              className="tw-w-5 tw-h-5 tw-accent-affinicar-primary tw-cursor-pointer"
                            />
                            Proposée
                          </label>
                          <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-400">
                            <input type="checkbox" disabled checked={false} className="tw-w-5 tw-h-5 tw-cursor-not-allowed tw-opacity-50" />
                            Acceptée
                          </label>
                        </div>
                      </div>
                    ))}

                    {externalWarranties.map((ext, index) => (
                      <div key={`ext-m-${index}`} className="tw-p-3 tw-bg-white tw-rounded-lg tw-border tw-border-gray-200">
                        <div className="tw-flex tw-items-start tw-justify-between tw-mb-2">
                          <div>
                            <p className="tw-text-sm tw-text-gray-900">
                              {[ext.externalName, ext.externalDuration ? `${ext.externalDuration} mois` : '', !hidePrices && ext.externalPrice ? `${ext.externalPrice}€ TTC` : ''].filter(Boolean).join(' - ') || 'Garantie externe'}
                            </p>
                            <span className="tw-text-xs tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-gray-100 tw-text-gray-500">Externe</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveExternal(index)}
                            className="tw-text-red-400 hover:tw-text-red-600 tw-p-2 tw-transition-colors tw-flex-shrink-0"
                            title="Supprimer"
                          >
                            <Trash2 className="tw-w-4 tw-h-4" />
                          </button>
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-4">
                          <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-500">
                            <input type="checkbox" disabled checked className="tw-w-5 tw-h-5 tw-cursor-not-allowed tw-opacity-50" />
                            Proposée
                          </label>
                          <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-400">
                            <input type="checkbox" disabled checked={false} className="tw-w-5 tw-h-5 tw-cursor-not-allowed tw-opacity-50" />
                            Acceptée
                          </label>
                        </div>
                      </div>
                    ))}

                    {!soldWarranty && otherWarranties.length === 0 && externalWarranties.length === 0 && (
                      <p className="tw-py-6 tw-text-center tw-text-gray-400 tw-text-sm">
                        Aucune {wording.singularLower} disponible
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Bouton ajouter garantie externe */}
              <div className="tw-mt-4">
                <button
                  type="button"
                  onClick={() => setShowExternalModal(true)}
                  className="tw-flex tw-items-center tw-justify-center sm:tw-justify-start tw-gap-1.5 tw-text-sm tw-font-medium tw-text-affinicar-primary hover:tw-text-affinicar-dark tw-transition-colors tw-w-full sm:tw-w-auto tw-py-2 sm:tw-py-0"
                >
                  <Plus className="tw-w-4 tw-h-4" />
                  Ajouter une garantie externe
                </button>
              </div>
            </div>
          </div>
        )}

      </form>

      </div>

      {/* Modale ajout garantie externe */}
      {showExternalModal && (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/50">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-[calc(100vw-2rem)] sm:tw-max-w-lg tw-mx-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b tw-border-gray-200">
              <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">Ajouter une garantie externe</h3>
              <button
                type="button"
                onClick={() => setShowExternalModal(false)}
                className="tw-text-gray-400 hover:tw-text-gray-600"
              >
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-px-6 tw-py-4 tw-space-y-4">
              <InputWithAddon
                label="Nom de la garantie"
                value={externalForm.externalName}
                onChange={(v) => setExternalForm((prev) => ({ ...prev, externalName: v }))}
                placeholder="Ex: Premium 24 mois"
              />

              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
                <InputWithAddon
                  label="Limite km"
                  value={externalForm.externalKmLimit}
                  onChange={(v) => setExternalForm((prev) => ({ ...prev, externalKmLimit: v }))}
                  type="number"
                  suffix="km"
                  placeholder="150000"
                  min={0}
                />
                <InputWithAddon
                  label="Plafond"
                  value={externalForm.externalFinancialCap}
                  onChange={(v) => setExternalForm((prev) => ({ ...prev, externalFinancialCap: v }))}
                  type="number"
                  suffix="€"
                  placeholder="5000"
                  min={0}
                />
              </div>

              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
                <InputWithAddon
                  label="Durée"
                  value={externalForm.externalDuration}
                  onChange={(v) => setExternalForm((prev) => ({ ...prev, externalDuration: v }))}
                  type="number"
                  suffix="mois"
                  placeholder="24"
                  min={0}
                />
                <InputWithAddon
                  label="Prix de vente TTC"
                  value={externalForm.externalPrice}
                  onChange={(v) => setExternalForm((prev) => ({ ...prev, externalPrice: v }))}
                  type="number"
                  suffix="€"
                  placeholder="890"
                  min={0}
                  step={0.01}
                />
              </div>

              <InputWithAddon
                label="Nombre de pièces garanties"
                value={externalForm.externalPieces}
                onChange={(v) => setExternalForm((prev) => ({ ...prev, externalPieces: v }))}
                type="number"
                placeholder="120"
                min={0}
              />
            </div>

            <div className="tw-flex tw-justify-end tw-gap-3 tw-px-6 tw-py-4 tw-border-t tw-border-gray-200">
              <button
                type="button"
                onClick={() => setShowExternalModal(false)}
                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl hover:tw-bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleAddExternal}
                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary tw-rounded-xl hover:tw-bg-affinicar-primary/90"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

Step2WarrantySelection.displayName = 'Step2WarrantySelection';
